/*
 * Video tickets page — what the hourly video ticket → Pipeline sync did, or
 * in dry run would do, for each video ticket of the active brand.
 *
 * The mode (off / dry run / live) is one server-wide switch, shown to
 * everyone and changeable by a super-admin only: it applies to every brand,
 * and live means the job writes to Notion and the master sheets.
 */

import { useMemo, useState } from "react";
import { Anchor, Box, Group, SegmentedControl, Select, Stack, Text } from "@mantine/core";
import {
  Alert,
  Button,
  EmptyState,
  PageHeader,
  Table,
  type Column,
} from "../components/ui";
import { useAuth } from "../auth/AuthContext";
import { useSetVideoSyncMode, useVideoSync } from "../api/queries";
import { errorMessage } from "../api/errors";
import type { VideoSyncMode, VideoSyncResponse, VideoSyncTicketRow } from "../api/client";
import { formatDateTime } from "../lib/format";
import { JourneyCell, PerformanceHover } from "../components/videoTickets/VideoJourney";
import { WebhookPanel } from "../components/videoTickets/WebhookPanel";

const MODE_LABEL: Record<VideoSyncMode, string> = {
  off: "Off",
  dry_run: "Dry run",
  live: "Live",
};

const MODE_MEANING: Record<VideoSyncMode, string> = {
  off: "The sync is not running.",
  dry_run:
    "The sync reads both boards every 40 minutes and records below what it would do. It writes nothing to Notion or the master sheets.",
  live: "The sync is creating and updating Pipeline tickets and master sheet rows every 40 minutes.",
};

/** Where a ticket has got to, in the words the team uses. */
export function stageLabel(row: VideoSyncTicketRow): string {
  if (row.last_error) return "Failed";
  if (row.stage >= 3) return "Launched";
  if (row.stage >= 2) return "Ready to Launch";
  if (row.missing.length > 0) return "Waiting on the team";
  if (row.stage === 1) return "In Production";
  // 2026-10-13: a video that is only requested (<PRODUCT> REQ + an Adname) gets
  // its Pipeline ticket at Copy Approved and waits there for an editor.
  if (
    row.notes.some((n) => n.startsWith("Video requested:")) ||
    /at Copy Approved|link(ed)? to the existing Pipeline ticket/.test(row.last_action ?? "")
  ) {
    return "Copy Approved (video requested)";
  }
  return "Not carried over";
}

/** The page's to-do groups, in the order they are shown. */
export type TicketGroup = "needs" | "requested" | "production" | "ready" | "launched" | "failed" | "other";

const GROUP_LABEL: Record<TicketGroup, string> = {
  needs: "Needs the team",
  requested: "Requested",
  production: "In production",
  ready: "Ready to launch",
  launched: "Launched",
  failed: "Failed",
  other: "Not carried over",
};

const GROUP_ORDER: TicketGroup[] = ["needs", "requested", "production", "ready", "launched", "failed", "other"];

/**
 * Notes that point at a real problem rather than reporting what happened
 * (2026-10-14): the sheet row will not be found at launch, the copy link
 * opens another concept's tab, or the batch does not match the videos.
 */
export function isWarning(note: string): boolean {
  return /^(At launch the sheet row|The copy link opens|Batch \S+ is \d+ ads, but)/.test(note);
}

/** Which group a ticket belongs in. A warning puts a ticket in front of the team too. */
export function ticketGroup(row: VideoSyncTicketRow): TicketGroup {
  if (row.last_error) return "failed";
  // Launched is done: nothing about it needs the team any more.
  if (row.stage >= 3) return "launched";
  if (row.missing.length > 0 || row.notes.some(isWarning)) return "needs";
  if (row.stage >= 2) return "ready";
  if (row.stage === 1) return "production";
  if (stageLabel(row).startsWith("Copy Approved")) return "requested";
  return "other";
}

/** "3 days", "5 hours", "under an hour": how long a ticket has been in its state. */
export function waitingFor(since: string | null | undefined, now: number = Date.now()): string | null {
  if (!since) return null;
  const ms = now - Date.parse(since);
  if (!Number.isFinite(ms) || ms < 0) return null;
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 1) return "under an hour";
  if (hours < 48) return `${hours} hour${hours === 1 ? "" : "s"}`;
  return `${Math.floor(hours / 24)} days`;
}

/** How long a ticket has sat in its stage, coloured by how stale that is. */
export function stageAge(
  since: string | null | undefined,
  now: number = Date.now(),
): { text: string; color: string | undefined } | null {
  const text = waitingFor(since, now);
  if (!text || !since) return null;
  const days = (now - Date.parse(since)) / 86_400_000;
  if (days < 1) return { text: `New · ${text}`, color: "teal.7" };
  if (days > 7) return { text: `In this stage ${text}`, color: "red.7" };
  if (days > 3) return { text: `In this stage ${text}`, color: "orange.7" };
  return { text: `In this stage ${text}`, color: undefined };
}

/** Where to go to check one warning. */
export function warningLink(row: VideoSyncTicketRow, warning: string): string | null {
  if (warning.startsWith("At launch the sheet row")) return row.sheet_url;
  if (warning.startsWith("The copy link opens")) return row.copy_url ?? row.pipeline_url;
  if (warning.startsWith("Batch ")) return row.video_url;
  return null;
}

/** Where to go to fix one "still needed" item. */
export function fixLink(row: VideoSyncTicketRow, item: string): string | null {
  if (item.startsWith("Videos")) return row.video_url;
  if (item.startsWith("Ad copy")) return row.copy_url ?? row.pipeline_url;
  if (item.startsWith("Master sheet")) return row.sheet_url;
  return row.pipeline_url;
}

const PRODUCT_KEY = "videoTickets.product";
const ALL = "all";

function readStoredProduct(): string {
  try {
    return window.localStorage.getItem(PRODUCT_KEY) ?? ALL;
  } catch {
    return ALL;
  }
}

function storeProduct(value: string): void {
  try {
    window.localStorage.setItem(PRODUCT_KEY, value);
  } catch {
    // Storage blocked (private window): the choice just isn't remembered.
  }
}

type LastRun = NonNullable<VideoSyncResponse["last_run"]>;

/**
 * What "Last checked" means for a row (2026-10-21). A ticket the last run saw
 * is current. One it did not see either belongs to a product that is not
 * syncing (the banner says why) or has left the statuses the sync follows,
 * usually because it went Live: finished, not stuck.
 */
export function seenState(
  row: Pick<VideoSyncTicketRow, "last_seen_at" | "product_key">,
  lastRun: LastRun | null,
): "current" | "product_failing" | "finished" {
  if (!lastRun) return "current";
  if (Date.parse(String(row.last_seen_at)) >= Date.parse(lastRun.started_at)) return "current";
  const product = lastRun.products.find((p) => p.product_key === row.product_key);
  if (lastRun.problem || (product && !product.ok)) return "product_failing";
  return "finished";
}

export function VideoTicketsPage() {
  const { isSuperAdmin } = useAuth();
  const query = useVideoSync();
  const setMode = useSetVideoSyncMode();
  const [picked, setPicked] = useState<VideoSyncMode | null>(null);

  const mode = query.data?.mode ?? null;
  const lastRun = query.data?.last_run ?? null;
  // Products the last run could not sync (2026-10-21): shouted about, never silent.
  const failing = (lastRun?.products ?? []).filter((p) => !p.ok);
  const brandProblem = lastRun?.problem && lastRun.problem !== "mode_off" ? lastRun.problem : null;
  const tickets = useMemo(() => query.data?.tickets ?? [], [query.data]);
  const pending = picked !== null && picked !== mode ? picked : null;

  const [product, setProduct] = useState<string>(readStoredProduct);
  const [group, setGroup] = useState<TicketGroup>("needs");
  const products = useMemo(
    () => [...new Set(tickets.map((t) => t.product_key))].sort(),
    [tickets],
  );
  // A remembered product that has no tickets any more falls back to all.
  const activeProduct = product !== ALL && products.includes(product) ? product : ALL;
  const forProduct = useMemo(
    () => (activeProduct === ALL ? tickets : tickets.filter((t) => t.product_key === activeProduct)),
    [tickets, activeProduct],
  );
  const counts = useMemo(() => {
    const c = Object.fromEntries(GROUP_ORDER.map((g) => [g, 0])) as Record<TicketGroup, number>;
    for (const t of forProduct) c[ticketGroup(t)] += 1;
    return c;
  }, [forProduct]);
  const shown = useMemo(() => {
    const rows = forProduct.filter((t) => ticketGroup(t) === group);
    // Longest-waiting first: the oldest problem is the one to chase.
    return [...rows].sort((a, b) => Date.parse(a.state_since ?? a.last_seen_at) - Date.parse(b.state_since ?? b.last_seen_at));
  }, [forProduct, group]);

  const columns: Column<VideoSyncTicketRow>[] = [
    {
      key: "ticket",
      header: "Video ticket",
      render: (row) => {
        const name = row.concept ?? (row.mal_number ? `MAL ${row.mal_number}` : "Untitled");
        return (
          <Stack gap={0}>
            <PerformanceHover row={row}>
              {row.video_url ? (
                <Anchor href={row.video_url} target="_blank" rel="noreferrer" fz="sm">
                  {name}
                </Anchor>
              ) : (
                <Text fz="sm">{name}</Text>
              )}
            </PerformanceHover>
            <Text c="dimmed" fz="xs">
              {row.product_key}
              {row.mal_number ? ` · MAL ${row.mal_number}` : ""}
            </Text>
            {row.editor && (
              <Text c="dimmed" fz="xs">
                Editor: {row.editor}
              </Text>
            )}
          </Stack>
        );
      },
    },
    {
      key: "stage",
      header: "Status",
      render: (row) => (
        <Stack gap={0}>
          <Text fz="sm" c={row.last_error ? "red" : undefined}>
            {stageLabel(row)}
          </Text>
          {(() => {
            const age = row.stage >= 3 ? null : stageAge(row.state_since);
            return age ? (
              <Text fz="xs" c={age.color ?? "dimmed"} fw={age.color ? 500 : undefined}>
                {age.text}
              </Text>
            ) : null;
          })()}
          {row.pipeline_url && (
            <Anchor href={row.pipeline_url} target="_blank" rel="noreferrer" fz="xs">
              {row.created_pipeline ? "Pipeline ticket (made by the sync)" : "Pipeline ticket"}
            </Anchor>
          )}
        </Stack>
      ),
      width: "190px",
    },
    {
      key: "journey",
      header: "Journey",
      render: (row) => <JourneyCell row={row} />,
      width: "170px",
    },
    {
      key: "did",
      header: "Last run",
      render: (row) =>
        row.last_error ? (
          <Text fz="xs" c="red">
            {row.last_error}
          </Text>
        ) : row.last_action ? (
          <Text fz="xs">{row.last_action}</Text>
        ) : (
          <Text fz="xs" c="dimmed">
            No changes
          </Text>
        ),
      width: "200px",
    },
    {
      key: "todo",
      header: "To do",
      render: (row) => {
        const warnings = row.notes.filter(isWarning);
        const info = row.notes.filter((n) => !isWarning(n));
        if (row.missing.length === 0 && warnings.length === 0 && info.length === 0) {
          return (
            <Text component="span" c="dimmed" fz="xs">
              —
            </Text>
          );
        }
        return (
          <Stack gap={2}>
            {row.missing.map((m) => {
              const href = fixLink(row, m);
              return href ? (
                <Anchor key={m} href={href} target="_blank" rel="noreferrer" fz="xs">
                  • {m}
                </Anchor>
              ) : (
                <Text key={m} fz="xs">
                  • {m}
                </Text>
              );
            })}
            {warnings.map((w) => {
              const href = warningLink(row, w);
              const text = `Check: ${w}`;
              return href ? (
                <Anchor key={w} href={href} target="_blank" rel="noreferrer" fz="xs" c="orange.8" fw={500}>
                  ⚠ {text}
                </Anchor>
              ) : (
                <Text key={w} fz="xs" c="orange.8" fw={500}>
                  ⚠ {text}
                </Text>
              );
            })}
            {info.map((n) => (
              <Text key={n} fz="xs" c="dimmed">
                {n}
              </Text>
            ))}
          </Stack>
        );
      },
    },
    {
      key: "videos",
      header: "Videos",
      render: (row) => (
        <Stack gap={0}>
          <Text fz="xs">{row.video_count ?? "—"}</Text>
          {row.batch && (
            <Text fz="xs" c="dimmed">
              {row.batch}
            </Text>
          )}
        </Stack>
      ),
      width: "110px",
    },
    {
      key: "seen",
      header: "Last checked",
      render: (row) => {
        const state = seenState(row, lastRun);
        return (
          <Stack gap={2}>
            <Text component="span" c="dimmed" fz="xs">
              {formatDateTime(row.last_seen_at)}
              {row.dry_run ? " · dry run" : ""}
            </Text>
            {state === "product_failing" && (
              <Text component="span" c="red" fz="xs">
                Not checked since: this product is not syncing
              </Text>
            )}
            {state === "finished" && (
              <Text component="span" c="dimmed" fz="xs">
                Finished: no longer synced (moved past Sent To Biddable)
              </Text>
            )}
          </Stack>
        );
      },
      width: "170px",
    },
  ];

  return (
    <>
      <PageHeader
        title="Video tickets"
        description="Video Production tickets carried onto the Pipeline: In Production when the video enters production, Ready to Launch when it is delivered and nothing is missing. Checked every 40 minutes."
      />

      {query.isError && (
        <Box mb="md">
          <Alert tone="error">Couldn't load video tickets: {errorMessage(query.error)}</Alert>
        </Box>
      )}

      {(brandProblem || failing.length > 0) && (
        <Box mb="md">
          <Alert tone="error">
            <Stack gap={4}>
              {brandProblem && (
                <Text fz="sm">
                  <strong>The sync stopped for this brand.</strong> {brandProblem}
                </Text>
              )}
              {failing.map((p) => (
                <Text fz="sm" key={p.product_key}>
                  <strong>{p.product_key.toUpperCase()} is not syncing</strong>
                  {p.since ? ` since ${formatDateTime(p.since)}` : ""}: {p.reason}
                  {p.last_ok_at ? ` Last synced ${formatDateTime(p.last_ok_at)}.` : ""}
                </Text>
              ))}
              <Text fz="xs" c="dimmed">
                No new video tickets are carried onto the Pipeline for these until it is fixed.
              </Text>
            </Stack>
          </Alert>
        </Box>
      )}

      {mode !== null && (
        <Box mb="lg">
          <Alert tone={mode === "live" ? "info" : "warning"}>
            <Stack gap="xs">
              <Text fz="sm">
                <strong>{MODE_LABEL[mode]}.</strong> {MODE_MEANING[mode]}
              </Text>
              {isSuperAdmin && (
                <Group gap="sm" align="flex-end">
                  <Select
                    aria-label="Video sync mode"
                    data={(["off", "dry_run", "live"] as VideoSyncMode[]).map((m) => ({
                      value: m,
                      label: MODE_LABEL[m],
                    }))}
                    value={picked ?? mode}
                    allowDeselect={false}
                    onChange={(v) => setPicked((v ?? mode) as VideoSyncMode)}
                    comboboxProps={{ withinPortal: true }}
                    w={160}
                  />
                  {pending && (
                    <Button
                      variant="primary"
                      disabled={setMode.isPending}
                      onClick={() => setMode.mutate(pending, { onSuccess: () => setPicked(null) })}
                    >
                      {setMode.isPending ? "Saving…" : `Switch to ${MODE_LABEL[pending].toLowerCase()}`}
                    </Button>
                  )}
                </Group>
              )}
              {pending === "live" && (
                <Text fz="xs">
                  Live applies to every brand. From the next check the sync will create
                  Pipeline tickets, add master sheet rows, reserve batch numbers and comment on
                  tickets.
                </Text>
              )}
              {setMode.isError && (
                <Text fz="xs" c="red">
                  Couldn't change the mode: {errorMessage(setMode.error)}
                </Text>
              )}
              {isSuperAdmin && <WebhookPanel />}
            </Stack>
          </Alert>
        </Box>
      )}

      {query.isLoading ? (
        <EmptyState icon="◷" title="Loading video tickets…" />
      ) : tickets.length === 0 ? (
        <EmptyState
          icon="▶"
          title="No video tickets yet"
          description="Tickets appear here after the next check finds one in production or delivered."
        />
      ) : (
        <>
          <Box mb="sm" style={{ overflowX: "auto" }} pb={4}>
            <SegmentedControl
              aria-label="Product"
              value={activeProduct}
              onChange={(v) => {
                setProduct(v);
                storeProduct(v);
              }}
              data={[
                { value: ALL, label: "All products" },
                ...products.map((p) => ({ value: p, label: p.toUpperCase() })),
              ]}
            />
          </Box>
          <Box mb="md" style={{ overflowX: "auto" }} pb={4}>
            <SegmentedControl
              aria-label="Ticket group"
              value={group}
              onChange={(v) => setGroup(v as TicketGroup)}
              data={GROUP_ORDER.map((g) => ({ value: g, label: `${GROUP_LABEL[g]} (${counts[g]})` }))}
            />
          </Box>
          {shown.length === 0 ? (
            <EmptyState
              icon="✓"
              title={`Nothing in ${GROUP_LABEL[group].toLowerCase()}`}
              description={
                group === "needs"
                  ? "No video ticket is waiting on the team right now."
                  : "Pick another group above to see the rest."
              }
            />
          ) : (
            <Table columns={columns} rows={shown} rowKey={(row) => row.id} />
          )}
        </>
      )}
    </>
  );
}
